/**
 * Sample-accurate Clip playback.
 *
 * ADR 0002 explains at length why HTMLAudioElement cannot do this. The short
 * version: on the 0.5s opening Clip, `play()`'s 20–200ms startup latency and the
 * ~250ms `timeupdate` cadence a main-thread stop rides on are not rounding
 * errors — the latter is half the rung.
 *
 * `AudioBufferSourceNode.start(when, offset, duration)` is scheduled on the
 * audio thread and is immune to main-thread jank, so a slow render frame cannot
 * hand the player more audio than they paid a Step for.
 */

/** Scheduling a hair ahead avoids racing the audio thread on the first Step. */
const SCHEDULE_LEAD_SECONDS = 0.02;

export interface ClipPlayer {
  /** Must be called from a user gesture — autoplay policy blocks it otherwise. */
  unlock: () => Promise<void>;
  play: (buffer: AudioBuffer, durationSeconds: number, startSeconds?: number) => void;
  /** 0–1. Takes effect on the next Step and on any Step already sounding. */
  setVolume: (volume: number) => void;
  stop: () => void;
  readonly context: AudioContext;
}

export function createClipPlayer(contextFactory: () => AudioContext = () => new AudioContext()): ClipPlayer {
  const context = contextFactory();
  let current: AudioBufferSourceNode | null = null;

  /**
   * One gain node for the whole player, not one per Step.
   *
   * Sources are created and thrown away per Step, so per-source gain would reset
   * the volume every time. This also means a change lands mid-Step rather than
   * waiting for the next one.
   */
  const gain = context.createGain?.();
  gain?.connect(context.destination);
  const output: AudioNode = gain ?? context.destination;

  function stop() {
    if (!current) return;
    try {
      current.stop();
    } catch {
      // Already stopped; harmless.
    }
    current.disconnect();
    current = null;
  }

  return {
    context,

    async unlock() {
      if (context.state === "suspended") await context.resume();
    },

    play(buffer, durationSeconds, startSeconds = 0) {
      stop();

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(output);

      // From the Track's hook, for exactly the Step's duration.
      //
      // The hook slides *back* rather than the Step being truncated: a 30s Step
      // on a 30s preview must play all 30s, not 30 minus the hook. Sliding also
      // keeps the rungs nested — a longer window that has been pulled back still
      // contains the shorter one it grew from.
      const duration = Math.min(durationSeconds, buffer.duration);
      const latestStart = Math.max(0, buffer.duration - duration);
      const start = Math.min(Math.max(startSeconds, 0), latestStart);
      source.start(context.currentTime + SCHEDULE_LEAD_SECONDS, start, duration);

      source.onended = () => {
        if (current === source) current = null;
      };
      current = source;
    },

    setVolume(volume) {
      if (!gain) return;
      // Clamped here as well as at the storage boundary: gain above 1 clips, and
      // a negative gain inverts the waveform.
      gain.gain.value = Math.min(1, Math.max(0, volume));
    },

    stop,
  };
}
